// Organization (SM-712): the node's profile, business settings and
// lifecycle status — READ-ONLY. Staff hold no lifecycle, move, settings,
// fee-plan or share-plan capability (plan D61/D62, SM-678 AC): those are
// exchange operations, and this screen says so instead of offering a verb
// the server would refuse. The status comes with a plain explanation of
// what it permits, and the audit trail shows who changed what and why.
//
// The ONE editable piece is the "Currency image" card (SM-978 / SM-983):
// the GOLD coin this node's users see, which an OWNER or ADMINISTRATOR of
// the node or of an ancestor (BRANDING_MANAGE) uploads and removes here.
// Everyone else sees it read-only.
//
//   GET /api/broker/{id}          BrokerItem (staff view: no payout_info)
//   GET /api/broker/{id}/audit    BrokerAuditListResponse (paginated)
//   GET|POST|DELETE /api/broker/{id}/currency-image   (CurrencyImageCard)
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { CurrencyImageCard } from "@/components/organization/CurrencyImageCard";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Id, KeyValues, Pager, TableFrame, Time } from "@/components/ui/Common";
import { byExchange } from "@/lib/actor";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { fetchAudit, fetchNode, keys } from "@/lib/queries";
import { brokerStatusExplanation, brokerStatusTone, restrictedByAncestor } from "@/lib/status";
import type { BrokerItem, JSONObject } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

export const Route = createFileRoute("/_app/organization")({
  beforeLoad: capabilityGuard("VIEW"),
  component: OrganizationPage,
});

const AUDIT_LIMIT = 20;

function OrganizationPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId } = useScope();
  const probe = { queryKey: keys.node(nodeId), queryFn: () => fetchNode(nodeId) };
  const nodeQ = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const [audit, setAudit] = useState({ nodeId, offset: 0 });
  // another node's trail starts at its first page (SM-957)
  const auditOffset = audit.nodeId === nodeId ? audit.offset : 0;
  const setAuditOffset = (offset: number) => setAudit({ nodeId, offset });
  const auditQ = useQuery({
    queryKey: keys.audit(nodeId, { offset: auditOffset, limit: AUDIT_LIMIT }),
    queryFn: () => fetchAudit(nodeId, { offset: auditOffset, limit: AUDIT_LIMIT }),
    enabled: guard.kind === "allow",
  });

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("org.title")} subtitle={t("org.subtitle")} coverage="none" />
      <div className="space-y-4">
        <QueryBlock query={nodeQ} lines={6}>
          {(b) => (
            <>
              <Profile broker={b} />
              <div className="mt-4">
                <CurrencyImageCard broker={b} />
              </div>
            </>
          )}
        </QueryBlock>
        <SectionCard title={t("org.audit.title")}>
          <QueryBlock
            query={auditQ}
            empty={(d) => ((d.items ?? []).length === 0 && !d.page?.total ? <EmptyState title={t("org.audit.empty")} /> : null)}
          >
            {(d) => (
              <>
                {/* a page past the end (rows gone since it was opened) keeps the pager to step back */}
                {(d.items ?? []).length === 0 ? (
                  <EmptyState title={t("org.audit.empty")} />
                ) : (
                <TableFrame label={t("org.audit.title")}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th scope="col">{t("common.when")}</th>
                        <th scope="col">{t("org.audit.action")}</th>
                        <th scope="col">{t("org.audit.entity")}</th>
                        <th scope="col">{t("org.audit.actor")}</th>
                        <th scope="col">{t("common.reason")}</th>
                        <th scope="col">{t("org.audit.event")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(d.items ?? []).map((e) => (
                        <tr key={e.event_id}>
                          <td className="text-xs">
                            <Time ms={e.occurred_at_unix_ms} />
                          </td>
                          <td className="text-xs" title={e.action}>
                            {labelOf(t, "auditAction", e.action)}
                          </td>
                          <td className="text-xs">
                            <span className="text-t-3">{labelOf(t, "auditEntity", e.entity_kind)}</span> <Id value={e.entity_id} />
                          </td>
                          <td className="text-xs">
                            {byExchange(e.actor_id, e.actor_kind) ? (
                              <span className="text-t-3">{t("actor.exchange")}</span>
                            ) : (
                              <>
                                <span className="text-t-3">{labelOf(t, "actorKind", e.actor_kind)}</span>{" "}
                                {e.actor_name ? <span className="text-t-1" title={e.actor_id}>{e.actor_name}</span> : <Id value={e.actor_id} />}
                              </>
                            )}
                          </td>
                          <td className="max-w-64 text-xs">{e.reason || "—"}</td>
                          <td>
                            <Id value={e.event_id} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableFrame>
                )}
                <Pager page={d.page} onOffset={setAuditOffset} />
              </>
            )}
          </QueryBlock>
        </SectionCard>
      </div>
    </Guarded>
  );
}

function Profile({ broker }: { broker: BrokerItem }) {
  const { t } = useI18n();
  const { node } = useScope();
  const effective = broker.effective_status || node?.effective_status || broker.status;
  const explanation = brokerStatusExplanation(effective);
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <SectionCard title={t("org.status.title")} className="lg:col-span-1">
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-2xs uppercase tracking-wide text-t-3">{t("org.status.own")}</span>
            <Badge tone={brokerStatusTone(broker.status)}>{t(`brokerStatus.${broker.status}`)}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-2xs uppercase tracking-wide text-t-3">{t("org.status.effective")}</span>
            <Badge tone={brokerStatusTone(effective)}>{t(`brokerStatus.${effective}`)}</Badge>
          </div>
          {restrictedByAncestor(broker.status, effective) && (
            <p className="banner warn mb-0 text-xs">{t("org.status.restricted")}</p>
          )}
          {explanation && <p className="text-t-2">{t(explanation)}</p>}
          {broker.status_reason && (
            <p className="text-xs text-t-3">{t("org.status.reason", { reason: broker.status_reason })}</p>
          )}
          <p className="text-xs text-t-3">
            {t("org.status.changedAt")} <Time ms={broker.status_changed_at_unix_ms} />
          </p>
          <p className="rounded-md border border-dashed border-line-2 p-2 text-xs text-t-3">{t("org.exchangeOperated")}</p>
        </div>
      </SectionCard>
      <SectionCard title={t("org.profile.title")} className="lg:col-span-2">
        <KeyValues
          rows={[
            { label: t("org.profile.displayName"), value: broker.display_name },
            { label: t("org.profile.legalName"), value: broker.legal_name },
            { label: t("org.profile.brokerId"), value: <span className="mono text-xs">{broker.broker_id}</span> },
            {
              label: t("org.profile.parent"),
              value: broker.parent_broker_id ? <span className="mono text-xs">{broker.parent_broker_id}</span> : t("org.profile.isRoot"),
            },
            { label: t("org.profile.root"), value: <span className="mono text-xs">{broker.root_broker_id}</span> },
            { label: t("org.profile.depth"), value: String(broker.broker_depth) },
            { label: t("org.profile.settingsVersion"), value: String(broker.settings_version) },
            { label: t("org.profile.hierarchyVersion"), value: String(broker.hierarchy_version) },
            { label: t("org.profile.created"), value: <Time ms={broker.created_at_unix_ms} /> },
            { label: t("org.profile.updated"), value: <Time ms={broker.updated_at_unix_ms} /> },
          ]}
        />
      </SectionCard>
      <SectionCard title={t("org.contacts")} className="lg:col-span-1">
        <JsonFields value={broker.contacts} empty={t("org.noContacts")} />
      </SectionCard>
      <SectionCard title={t("org.settings")} className="lg:col-span-2">
        <JsonFields value={broker.business_settings} empty={t("org.noSettings")} />
      </SectionCard>
    </div>
  );
}

/** A JSON object's top-level fields as a key/value list (nested values as JSON). */
function JsonFields({ value, empty }: { value: JSONObject | null | undefined; empty: string }) {
  const entries = value && typeof value === "object" && !Array.isArray(value) ? Object.entries(value) : [];
  if (entries.length === 0) return <p className="text-sm text-t-3">{empty}</p>;
  return (
    <KeyValues
      rows={entries.map(([k, v]) => ({
        label: k,
        value:
          v !== null && typeof v === "object" ? (
            <pre className="mono whitespace-pre-wrap break-words text-2xs text-t-2">{JSON.stringify(v, null, 2)}</pre>
          ) : (
            <span className="break-words">{v === null || v === undefined || v === "" ? "—" : String(v)}</span>
          ),
      }))}
    />
  );
}
